import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, TrendingUp, Check, X, Minus } from 'lucide-react';

const L = {
  right: { sv: 'Rätt', en: 'Correct', ar: 'صحيحة', tr: 'Doğru', fa: 'درست', so: 'Sax', pl: 'Dobrze', es: 'Correctas', de: 'Richtig' },
  wrong: { sv: 'Fel', en: 'Wrong', ar: 'خاطئة', tr: 'Yanlış', fa: 'غلط', so: 'Khalad', pl: 'Źle', es: 'Incorrectas', de: 'Falsch' },
  skipped: { sv: 'Obesvarade', en: 'Unanswered', ar: 'بدون إجابة', tr: 'Boş', fa: 'بی‌پاسخ', so: 'Lama jawaabin', pl: 'Bez odpowiedzi', es: 'Sin responder', de: 'Unbeantwortet' },
  failed: { sv: 'Ej godkänd', en: 'Not passed', ar: 'لم تنجح', tr: 'Geçilmedi', fa: 'قبول نشدی', so: 'Kama gudbin', pl: 'Nie zdano', es: 'No aprobado', de: 'Nicht bestanden' },
  passed: { sv: 'Godkänd', en: 'Passed', ar: 'ناجح', tr: 'Geçti', fa: 'قبول', so: 'Waa gudbay', pl: 'Zdano', es: 'Aprobado', de: 'Bestanden' },
  rule: { sv: 'För godkänt krävs {n} rätt av {t}', en: '{n} of {t} correct needed to pass', ar: 'للنجاح تحتاج {n} إجابة صحيحة من {t}', tr: 'Geçmek için {t} sorudan {n} doğru gerekir', fa: 'برای قبولی {n} پاسخ درست از {t} لازم است', so: '{n} sax ah oo {t} ka mid ah ayaa loo baahan yahay', pl: 'Aby zdać, potrzeba {n} z {t} poprawnych', es: 'Necesitas {n} de {t} correctas para aprobar', de: '{n} von {t} richtig zum Bestehen nötig' },
};

const COLORS = ['#10b981', '#34d399', '#6ee7b7', '#fbbf24', '#0ea5e9', '#ffffff'];

const Confetti = () => (
  <div className="pointer-events-none fixed inset-0 z-[60] overflow-hidden" data-testid="pass-confetti" aria-hidden>
    {Array.from({ length: 60 }).map((_, i) => (
      <span key={i} className="dm-confetti" style={{
        left: `${(i * 37) % 100}%`, background: COLORS[i % COLORS.length],
        animationDelay: `${(i % 12) * 0.06}s`, animationDuration: `${1.6 + (i % 5) * 0.25}s`,
      }} />
    ))}
  </div>
);

const useCountUp = (target) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf; const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 900);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return v;
};

const Chip = ({ icon: Icon, value, label, tone, testId }) => (
  <div className="flex-1 min-w-0 rounded-2xl bg-white/15 ring-1 ring-white/20 backdrop-blur-md px-2 py-2.5" data-testid={testId}>
    <div className="flex items-center justify-center gap-1.5">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center ${tone}`}><Icon className="w-3 h-3" strokeWidth={3} /></span>
      <span className="text-xl font-extrabold tabular-nums leading-none text-white">{value}</span>
    </div>
    <div className="mt-1 text-[11px] font-medium text-white/80 truncate">{label}</div>
  </div>
);

export const ResultHero = ({ refEl, passed, results, title, message, trialNote, unanswered = 0 }) => {
  const { i18n } = useTranslation();
  const tx = (k) => L[k][i18n.language] || L[k].en;
  const pct = results.score_percentage || 0;
  const shown = useCountUp(pct);
  const [party, setParty] = useState(passed);
  useEffect(() => { if (!passed) return; const id = setTimeout(() => setParty(false), 3200); return () => clearTimeout(id); }, [passed]);
  const total = results.total_questions || 0;
  const right = results.correct_answers || 0;
  const skipped = Math.min(unanswered, total - right);
  const wrong = total - right - skipped;
  const need = results.pass_count || Math.ceil(total * 0.8);
  const fill = total ? (right / total) * 100 : 0;
  const mark = total ? (need / total) * 100 : 80;

  return (
    <div ref={refEl} className="relative overflow-hidden bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white px-5 pt-5 pb-4">
      {party && <Confetti />}
      <div className="absolute -top-24 -end-20 w-64 h-64 rounded-full bg-white/15 blur-3xl" aria-hidden />
      <div className="absolute -bottom-24 -start-16 w-56 h-56 rounded-full bg-teal-300/25 blur-3xl" aria-hidden />
      <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:16px_16px] [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" aria-hidden />

      <div className="relative flex items-start justify-between gap-3 text-start">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">{title}</p>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-5xl font-black tracking-tighter tabular-nums leading-none" data-testid="score">{shown}</span>
            <span className="text-2xl font-bold text-white/80">%</span>
          </div>
          <p className="mt-1.5 text-[13px] text-white/85 leading-snug" data-testid="result-message">{message}</p>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold shadow-lg ${passed ? 'bg-white text-emerald-700' : 'bg-amber-400 text-amber-950'}`} data-testid="result-status-badge">
          {passed ? <Sparkles className="w-3.5 h-3.5" /> : <TrendingUp className="w-3.5 h-3.5" />}
          {passed ? tx('passed') : tx('failed')}
        </span>
      </div>

      <div className="relative mt-4" data-testid="result-progress">
        <div className="h-2.5 w-full rounded-full bg-black/20 overflow-hidden">
          <div className="h-full rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.8)] transition-[width] duration-1000 ease-out" style={{ width: `${shown ? fill : 0}%` }} />
        </div>
        <div className="absolute -top-1 h-[18px] w-[3px] rounded-full bg-amber-300 ring-2 ring-emerald-700" style={{ insetInlineStart: `calc(${mark}% - 1.5px)` }} />
        <p className="mt-1.5 text-[11px] font-medium text-white/80 text-start" data-testid="result-pass-rule">
          {tx('rule').replace('{n}', need).replace('{t}', total)}
        </p>
      </div>

      <div className="relative mt-3 flex gap-2">
        <Chip icon={Check} value={right} label={tx('right')} tone="bg-white text-emerald-600" testId="result-correct-count" />
        <Chip icon={X} value={wrong} label={tx('wrong')} tone="bg-rose-500 text-white" testId="result-wrong-count" />
        {skipped > 0 && <Chip icon={Minus} value={skipped} label={tx('skipped')} tone="bg-white/30 text-white" testId="result-unanswered-count" />}
      </div>
      {trialNote && <p className="relative text-[11px] text-white/75 mt-2 text-center" data-testid="trial-note">{trialNote}</p>}
      <p className="relative mt-3 text-[10px] font-semibold tracking-[0.25em] text-white/50 text-center">DRIVEMASTER.SE</p>
    </div>
  );
};
